import { useEffect, useState, useSyncExternalStore } from 'react';
import {
  Compositor,
  DecoderPool,
  Player,
  type AssetStatus,
  type AssetResolver,
  type PlayerState,
} from '@/engine';

export interface Engine {
  compositor: Compositor;
  pool: DecoderPool;
  player: Player;
}

/**
 * Owns the preview engine for an editor session. Initializes the WebGL
 * compositor (async) and tears everything down on unmount. `resolver` must be
 * stable (wrap in useCallback in the caller).
 */
export function useEngine(resolver: AssetResolver): Engine | null {
  const [engine, setEngine] = useState<Engine | null>(null);

  useEffect(() => {
    let disposed = false;
    const compositor = new Compositor();
    const pool = new DecoderPool(resolver);
    const player = new Player(compositor, pool);
    void compositor.init().then(() => {
      if (disposed) {
        compositor.dispose();
        return;
      }
      setEngine({ compositor, pool, player });
    });
    return () => {
      disposed = true;
      setEngine(null);
      player.dispose();
      pool.dispose();
      // compositor disposed above if init lost the race; otherwise dispose now.
      try {
        compositor.dispose();
      } catch {
        /* already destroyed */
      }
    };
  }, [resolver]);

  return engine;
}

const EMPTY: PlayerState = { currentFrame: 0, playing: false, durationInFrames: 0 };

/** Subscribe to player transport state (frame/playing/duration). */
export function usePlayerState(player: Player | null): PlayerState {
  return useSyncExternalStore(
    player ? player.subscribe : () => () => {},
    player ? player.getSnapshot : () => EMPTY
  );
}

const EMPTY_STATES: Record<string, AssetStatus> = {};

/** Subscribe to per-asset decoder load status (asset id → {state, progress}). */
export function useAssetStates(pool: DecoderPool | null): Record<string, AssetStatus> {
  return useSyncExternalStore(
    pool ? pool.subscribe : () => () => {},
    pool ? pool.getStates : () => EMPTY_STATES
  );
}
